import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { Heart, BookOpen, Clock, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSupabaseAuth } from '@/hooks/auth';
import { useUserFavorites } from '@/hooks/useUserFavorites';
import { ALL_COURSE_CATEGORIES } from '@/config/courseNavigation';


interface CourseDetails {
  id: string;
  title: string;
  level: string;
  duration: string;
  route: string;
  categoryTitle: string;
  categoryIcon: any;
}

const FavoritesSystem: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSupabaseAuth();
  const { favorites, isLoading, addFavorite, removeFavorite, isFavorite } = useUserFavorites();

  // Get course details from ALL_COURSE_CATEGORIES
  const getCourseDetails = (courseId: string, categoryId: string): CourseDetails | null => {
    const category = ALL_COURSE_CATEGORIES.find(cat => cat.id === categoryId);
    if (!category) return null;
    
    const course = category.courses.find(c => c.id === courseId);
    if (!course) return null;
    
    return {
      ...course,
      categoryTitle: category.title,
      categoryIcon: category.icon
    };
  };

  const toggleFavorite = async (courseId: string, categoryId: string) => {
    if (!user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to save favorites",
        variant: "destructive"
      });
      return;
    }

    // Store as composite key: "categoryId-courseId"
    const itemId = `${categoryId}-${courseId}`;

    if (isFavorite(itemId)) {
      removeFavorite(itemId);
    } else {
      addFavorite({ itemId, itemType: 'course' });
    }
  };

  const isFavoriteCourse = (courseId: string, categoryId: string) => {
    const itemId = `${categoryId}-${courseId}`;
    return isFavorite(itemId);
  };

  const favoriteCoursesWithDetails = favorites
    .map(fav => {
      // item_id format: "categoryId-courseId"
      const [category_id, course_id] = fav.item_id.split('-');
      const details = getCourseDetails(course_id, category_id);
      return details ? { ...fav, category_id, course_id, ...details } : null;
    })
    .filter(Boolean) as Array<typeof favorites[0] & { category_id: string; course_id: string } & CourseDetails>;

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <Card key={index}>
            <CardContent className="p-6">
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
                <div className="h-3 bg-muted rounded w-1/4"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">My Favorites</h2>
          <p className="text-muted-foreground">
            Courses you've saved for later
          </p>
        </div>
        <Badge variant="secondary" className="gap-1">
          <Heart className="h-3 w-3" />
          {favorites.length} favorites
        </Badge>
      </div>

      {favoriteCoursesWithDetails.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Heart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No favorites yet</h3>
            <p className="text-muted-foreground mb-4">
              Start exploring courses and save your favorites for quick access
            </p>
            <Button onClick={() => {
              sessionStorage.setItem('courseNavigationOrigin', 'courses');
              navigate('/courses');
            }} className="gap-2">
              <BookOpen className="h-4 w-4" />
              Browse Courses
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {favoriteCoursesWithDetails.map((course) => {
            const CategoryIcon = course.categoryIcon;
            return (
              <Card 
                key={`${course.category_id}-${course.course_id}`}
                className="cursor-pointer hover:shadow-md transition-shadow"
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <CategoryIcon className="h-5 w-5 text-primary" />
                      <Badge variant="outline" className="text-xs">
                        {course.categoryTitle}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs">
                        {course.level}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(course.course_id, course.category_id);
                        }}
                        className="h-8 w-8 p-0"
                      >
                        <Heart 
                          className="h-4 w-4 text-red-500 fill-red-500" 
                        />
                      </Button>
                    </div>
                  </div>
                  <CardTitle 
                    className="text-lg leading-tight cursor-pointer hover:text-primary"
                    onClick={() => navigate(course.route)}
                  >
                    {course.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {course.duration}
                      </div>
                      <div className="flex items-center gap-1">
                        <Star className="h-4 w-4" />
                        Favorite
                      </div>
                    </div>
                    <Button 
                      size="sm" 
                      onClick={() => navigate(course.route)}
                      className="gap-2"
                    >
                      <BookOpen className="h-3 w-3" />
                      Start Learning
                    </Button>
                  </div>
                  <div className="text-xs text-muted-foreground mt-2">
                    Added on {new Date(course.created_at).toLocaleDateString()}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Export hook wrapper for backward compatibility
export const useFavorites = () => {
  const { user } = useSupabaseAuth();
  const { favorites, addFavorite, removeFavorite, isFavorite } = useUserFavorites();

  const toggleFavorite = async (courseId: string, categoryId: string) => {
    if (!user) return false;
    
    const itemId = `${categoryId}-${courseId}`;

    if (isFavorite(itemId)) {
      removeFavorite(itemId);
      return false;
    } else {
      addFavorite({ itemId, itemType: 'course' });
      return true;
    }
  };

  const isFavoriteCourse = (courseId: string, categoryId: string) => {
    const itemId = `${categoryId}-${courseId}`;
    return isFavorite(itemId);
  };

  return {
    favorites,
    toggleFavorite,
    isFavorite: isFavoriteCourse,
    refetch: () => {}
  };
};

export default FavoritesSystem;